import { default as React } from 'react';
import { Route, Switch } from 'react-router-dom';
import { useSelectUserTeamAccess } from '../../selectors';
import { STATUS_ENUM, UserTeamAccessState } from '../../types';
import RenderComponentOrLoading from '../RenderComponentOrLoading';
import VerifyUserTeamAccessDialog from './VerifyUserTeamAccessDialog';
import { useSiteConfig } from '../../contexts/SiteConfiguration';
import { useLandingPageAccess } from '../../hooks';

const UserTeamAccess = (props: Omit<UserTeamAccessState, 'enabled'>) => {
	const { config } = useSiteConfig();
	const {
		canCreateInfo360Hubs,
		hasInfo360Licenses,
		hubExists,
		projectsExists,
		hubAccountId,
		status,
	} = props;

	if (canCreateInfo360Hubs && hasInfo360Licenses && !hubExists) {
		const manageHubsUrl = config?.forge?.cpaHubsDomain
			? config.forge.cpaHubsDomain
			: 'https://manage.autodesk.com/hubs-list';
		window.location.href = manageHubsUrl;
		return <></>;
	} else if (canCreateInfo360Hubs && hubExists && !projectsExists) {
		const manageProjectUrl = config?.forge?.cpaProjectsDomain
			? `${config.forge.cpaProjectsDomain}${hubAccountId}/active`
			: `https://cpa.autodesk.com/account-admin/projects/accounts/${hubAccountId}/active`;
		window.location.href = manageProjectUrl;
		return <></>;
	}

	return (
		<RenderComponentOrLoading loading={status === STATUS_ENUM.PENDING}>
			<Switch>
				<Route path="/">
					<VerifyUserTeamAccessDialog {...props} />
				</Route>
			</Switch>
		</RenderComponentOrLoading>
	);
};

const LandingPageAccess = ({ children }: { children: JSX.Element }) => {
	const {
		emptyProjects,
		accessLoading,
		projectsLoading,
		isHubAdmin,
		isTeamAdmin,
		getHubsListUrl,
		getCPAUrl,
	} = useLandingPageAccess();

	//user has projects display landing page
	if (!projectsLoading && !emptyProjects) return <>{children}</>;
	//there are no projects then redirect
	if (!accessLoading && emptyProjects && (isHubAdmin || isTeamAdmin)) {
		const redirectUrl = isHubAdmin ? getCPAUrl() : getHubsListUrl();

		window.location.href = redirectUrl;
		return <></>;
	}
	return (
		<RenderComponentOrLoading loading={accessLoading}>
			{emptyProjects && !isHubAdmin && !isTeamAdmin && (
				<Switch>
					<Route path="/">
						<VerifyUserTeamAccessDialog
							hasInfo360Licenses={null}
							projectsExists={false}
							hubExists={false}
							isLandingPage
						/>
					</Route>
				</Switch>
			)}
		</RenderComponentOrLoading>
	);
};

interface Props {
	isLandingPage?: boolean;
	children: JSX.Element;
}
export default function VerifyUserTeamAccessWrapper({
	isLandingPage = false,
	children,
}: Props) {
	const { enabled, ...restProps } = useSelectUserTeamAccess();

	if (enabled) {
		return <UserTeamAccess {...restProps} />;
	}
	if (isLandingPage) {
		return <LandingPageAccess>{children}</LandingPageAccess>;
	}

	return <>{children}</>;
}
